import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useContext, useState } from "react";
import TitleActionsContainer from "../../widgets/TitleActionsContainer";
import PageTitle from "../../widgets/PageTitle";
import SearchIcon from "@mui/icons-material/Search";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GroupIcon from "@mui/icons-material/Group";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { debounce } from "lodash";
import CustomCircularP from "../../widgets/CustomCircularP";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { getErrorResponse, getHeader, toastError } from "../../utils/util";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import useApiEp from "../../hooks/useApiEp";
import DataTable from "../../widgets/DataTable";
import IconMenu from "../../widgets/IconMenu";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { GlassButton } from "../../themes/UiElements";
import { UserContext } from "../../context/UserProvider";
import { useDialog } from "../../context/DialogContext";

// ── ActiveGroupBanner ─────────────────────────────────────────────────────────
const ActiveGroupBanner = ({ userData, loading, contactList, onGroupSet }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);

  const handleOpen = () => {
    setSelected(userData?.contactGroup ?? "");
    setDialogOpen(true);
  };
  const handleClose = () => setDialogOpen(false);

  const handleSave = async () => {
    if (!selected) return;
    try {
      setSaving(true);
      const response = await axios.post(
        `${API_ENDPOINT}/user/set-active-contact-group`,
        { groupName: selected },
        getHeader()
      );
      toast.success(response.data.message || "Active group updated!");
      onGroupSet();
      handleClose();
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <>
      {userData?.contactGroup ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            mb: 2,
            px: 2,
            py: 1,
            borderRadius: 2,
            bgcolor: "success.light",
            color: "success.contrastText",
          }}
        >
          <CheckCircleIcon fontSize="small" />
          <Typography variant="body2">
            Your current active contact group is&nbsp;
            <strong>{userData.contactGroup}</strong>.
          </Typography>
          <Button
            size="small"
            variant="text"
            sx={{ color: "inherit", textDecoration: "underline", p: 0, ml: 1 }}
            onClick={handleOpen}
          >
            Change
          </Button>
        </Stack>
      ) : (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            mb: 2,
            px: 2,
            py: 1,
            borderRadius: 2,
            bgcolor: "warning.light",
            color: "warning.contrastText",
          }}
        >
          <GroupIcon fontSize="small" />
          <Typography variant="body2">
            You have not set the active contact group.&nbsp;
          </Typography>
          <Button
            size="small"
            variant="text"
            sx={{
              color: "inherit",
              fontWeight: 700,
              textDecoration: "underline",
              p: 0,
            }}
            onClick={handleOpen}
          >
            Set now
          </Button>
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Select Active Contact Group</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {!contactList?.length ? (
            <Typography sx={{ p: 2 }} color="text.secondary">
              No groups available.
            </Typography>
          ) : (
            <List disablePadding>
              {contactList.map((group) => (
                <ListItem key={group} disablePadding>
                  <ListItemButton
                    selected={selected === group}
                    onClick={() => setSelected(group)}
                    sx={{
                      "&.Mui-selected": {
                        bgcolor: "primary.light",
                        color: "primary.contrastText",
                        "&:hover": { bgcolor: "primary.main" },
                      },
                    }}
                  >
                    <ListItemText primary={group} />
                    {selected === group && (
                      <CheckCircleIcon fontSize="small" color="primary" />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!selected || saving}
          >
            {saving ? "Saving…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ── MappedSchemaBanner ────────────────────────────────────────────────────────
/**
 * status 2  → no mapped schema; if there's an active contact group, nudge the
 *             user to map a schema so contacts can be enriched.
 * status 3  → schema exists; show a chip that opens a read-only modal with
 *             every primitive key→value pair from the schema object.
 */
const MappedSchemaBanner = ({ mappedSchema, hasActiveGroup }) => {
  const navigate = useNavigate();
  const [schemaOpen, setSchemaOpen] = useState(false);

  // Still loading or API returned status 1 (no google sheet) – nothing to show
  if (!mappedSchema || mappedSchema.status === 1) return null;

  // ── status 2: no mapped schema ────────────────────────────────────────────
  if (mappedSchema.status === 2) {
    // Only show the nudge when the user actually has contacts in a group
    if (!hasActiveGroup) return null;

    return (
      <Alert
        severity="info"
        icon={<InfoOutlinedIcon />}
        sx={{ mb: 2, alignItems: "center" }}
        action={
          <Button
            color="info"
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon fontSize="small" />}
            onClick={() => navigate("/home/map-sheet-schema")}
          >
            Map Schema
          </Button>
        }
      >
        <Typography variant="body2">
          Your active contact group has no sheet schema mapped yet. Map a schema
          to enable contact field enrichment from your Google Sheet.
        </Typography>
      </Alert>
    );
  }

  // ── status 3: schema present ──────────────────────────────────────────────
  if (mappedSchema.status === 3 && mappedSchema.schema) {
    // Flatten to primitive key-value pairs only
    const entries = Object.entries(mappedSchema.schema).filter(
      ([, v]) => v !== null && typeof v !== "object"
    );

    return (
      <>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Chip
            icon={<TableChartOutlinedIcon />}
            label="View Mapped Schema"
            variant="outlined"
            color="primary"
            clickable
            onClick={() => setSchemaOpen(true)}
            size="small"
          />
        </Stack>

        <Dialog
          open={schemaOpen}
          onClose={() => setSchemaOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <TableChartOutlinedIcon color="primary" />
              <span>Mapped Schema</span>
            </Stack>
          </DialogTitle>

          <DialogContent dividers sx={{ p: 0 }}>
            {entries.length === 0 ? (
              <Typography sx={{ p: 2 }} color="text.secondary">
                No primitive fields found in the schema.
              </Typography>
            ) : (
              <Table size="small">
                <TableBody>
                  {entries.map(([key, value], idx) => (
                    <TableRow
                      key={key}
                      sx={{
                        bgcolor: idx % 2 === 0 ? "action.hover" : "transparent",
                      }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "text.secondary",
                          width: "40%",
                          borderRight: "1px solid",
                          borderColor: "divider",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {key}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={String(value)}
                          size="small"
                          variant="outlined"
                          color={
                            typeof value === "boolean"
                              ? value
                                ? "success"
                                : "error"
                              : "default"
                          }
                          sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setSchemaOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return null;
};
// ─────────────────────────────────────────────────────────────────────────────

const ContactsList = () => {
  const { user: userData, loading, reloadUser } = useContext(UserContext);
  const [phoneQ, setPhoneQ] = useState("");
  const [groupName, setGroupName] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [deleting, setDeleting] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/user/get-contacts-group?searchphoneNumber=${phoneQ}&group=${groupName}`
  );
  const { data: contactList, isLoading: loadingList } =
    useApiEp(`/user/group-list`);
  const { data: mappedSchema, isLoading: loadingSchema } = useApiEp(
    `/user/get-active-mapped-schema`
  );

  const navigate = useNavigate();
  const { showDialog } = useDialog();

  const debouncedSearch = useCallback(
    debounce((value) => setPhoneQ(value), 500),
    []
  );

  const handleSearchChange = (value) => debouncedSearch(value);
  const handleMenuClose = () => setAnchorEl(null);

  const handleAction = useCallback((action, id) => {
    switch (action) {
      case "view":
        navigate(`/home/configurations/contacts/${id}/contact-detail`);
        break;
      case "delete":
        showDialog(
          "Please confirm!",
          "Are you sure you want to delete this Contact?",
          {
            type: "delete",
            onConfirm: async () => {
              handleDeleteContact(id);
            },
          }
        );

        break;
      default:
        console.warn("unknown action", id);
    }
  }, []);

  const handleDeleteContact = useCallback(async (itemId) => {
    try {
      setDeleting(true);
      const response = await axios.delete(
        `${API_ENDPOINT}/user/delete-contact/${itemId}`,
        getHeader()
      );
      toast.success(response.data.message);
      refetch();
    } catch (error) {
      toastError(error);
    } finally {
      setDeleting(false);
    }
  }, []);

  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  return (
    <Container sx={{ mt: 3 }}>
      <CustomCircularP show={isLoading || deleting} />

      <TitleActionsContainer>
        <PageTitle>
          Contacts List ({Number(data?.totalItems || 0).toLocaleString("en-IN")}
          )
        </PageTitle>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ width: { xs: "100%", sm: "auto" }, mt: { xs: 1, sm: 0 } }}
        >
          <Autocomplete
            size="small"
            loading={loadingList}
            sx={{ minWidth: 164 }}
            value={groupName}
            onChange={(_e, newV) => setGroupName(newV ?? "")}
            options={contactList || []}
            renderInput={(params) => (
              <TextField {...params} label="Group Name" />
            )}
          />
          <TextField
            sx={{ minWidth: 200 }}
            size="small"
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by Phone"
            InputProps={{ startAdornment: <SearchIcon fontSize="small" /> }}
          />
          <GlassButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            variant="outlined"
            startIcon={<AddOutlinedIcon />}
          >
            Add
          </GlassButton>
          <IconMenu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            menuItems={[
              {
                label: "Create Contact Group",
                icon: <GroupAddIcon />,
                onClick: () => {
                  handleMenuClose();
                  navigate(`/home/add-contact`);
                },
              },
              {
                label: "Add Contact List to Group",
                icon: <PlaylistAddIcon />,
                onClick: () => {
                  handleMenuClose();
                  navigate(`/home/add-contact-list`);
                },
              },
            ]}
          />
        </Stack>
      </TitleActionsContainer>

      {/* ── active-group banner ─────────────────────────────────────── */}
      <ActiveGroupBanner
        userData={userData}
        loading={loading}
        contactList={contactList}
        onGroupSet={reloadUser}
      />

      {/* ── mapped-schema banner / chip ─────────────────────────────── */}
      {!loadingSchema && (
        <MappedSchemaBanner
          mappedSchema={mappedSchema}
          hasActiveGroup={!!userData?.contactGroup}
        />
      )}

      <DataTable
        sx={{ mb: 1 }}
        columns={[
          {
            id: "serial",
            label: "S.No.",
            render: (_v, _row, idx) => (page - 1) * limit + idx + 1,
          },
          { id: "groupName", label: "Group" },
          { id: "phoneNumber", label: "Phone" },
          { id: "Name", label: "Name" },
          {
            id: "createdOn",
            label: "Created On",
            render: (v) =>
              new Date(v).toLocaleString("en-IN", { dateStyle: "medium" }),
          },
        ]}
        actions={["view", "delete"]}
        rows={data?.data ?? []}
        page={page}
        limit={limit}
        totalPages={data?.totalPages ?? 1}
        onPageChange={setPage}
        onAction={handleAction}
      />
    </Container>
  );
};

export default ContactsList;
